import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Calendar,
  CreditCard,
  Loader2,
  PlaneTakeoff,
  Clock,
  Check,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { reserveUrl } from '@/lib/apiUrls';
import { mergeReserveActAsHeaders } from '@/lib/reserveActAs';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { useReserveEligibility } from '@/hooks/useReserveEligibility';

const frequencyLabels = { weekly: 'Weekly', biweekly: 'Every 2 weeks', monthly: 'Monthly' };

function formatDuration(isoDuration) {
  if (isoDuration == null) return '';
  if (typeof isoDuration === 'number') {
    const h = Math.floor(isoDuration / 60);
    const m = isoDuration % 60;
    return `${h}h ${m}m`;
  }
  const matches = String(isoDuration).match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return '';
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return `${hours}h ${minutes}m`;
}

function formatTime(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'h:mm a');
}

function formatDate(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'EEE, MMM d');
}

function FlightSummaryCard({ flightData, label, bookingOption, searchParams }) {
  const slices = flightData?.slices || [];
  const cabinClass = searchParams?.cabin_class || 'economy';

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-white/40 text-xs uppercase tracking-wider mb-4">{label}</p>
      {slices.map((slice, idx) => {
        const segment = slice.segments?.[0];
        const lastSegment = slice.segments?.[slice.segments?.length - 1];
        if (!segment) return null;
        const marketingCarrier = segment.marketing_carrier;
        const stops = (slice.segments?.length || 1) - 1;
        const fareOption = idx === 0
          ? (bookingOption?.departing || bookingOption?.together)
          : (bookingOption?.returning || bookingOption?.together);
        const cabinDisplay = fareOption?.option_title || segment.passengers_cabin_class_marketing_name || cabinClass;

        return (
          <div key={idx} className="mb-6 last:mb-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-xs font-normal uppercase">
                {idx === 0 ? 'Departing' : 'Return'} • {formatDate(segment.departing_at)}
              </span>
              {stops > 0 && (
                <span className="text-brand-400/80 text-[10px] uppercase">{stops} stop{stops > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex justify-center overflow-hidden">
                  {marketingCarrier?.logo_symbol_url ? (
                    <img src={marketingCarrier.logo_symbol_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <PlaneTakeoff className="w-5 h-5 text-white/40" />
                  )}
                </div>
                <div>
                  <div className="text-white text-sm font-medium">
                    {formatTime(segment.departing_at)} – {formatTime(lastSegment?.arriving_at)}
                  </div>
                  <div className="text-white/40 text-xs">
                    {marketingCarrier?.name}
                    {segment.marketing_carrier_flight_number && ` ${segment.marketing_carrier_flight_number}`}
                  </div>
                  {cabinDisplay && (
                    <div className="text-white/30 text-xs capitalize">{cabinDisplay}</div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/50 text-xs uppercase">
                  {segment.origin?.iata_code}–{lastSegment?.destination?.iata_code}
                </div>
                <div className="text-white/60 text-xs">{formatDuration(slice.duration)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScheduleComparison({ original, newSchedule, frequency }) {
  const fmt = (c) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Original schedule</p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Per payment</span>
            <span className="text-white">${fmt(original?.per_payment_cents ?? 0)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Payments remaining</span>
            <span className="text-white">{original?.num_payments ?? 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Frequency</span>
            <span className="text-white">{frequencyLabels[frequency] || frequency}</span>
          </div>
          {original?.dates?.length > 0 && (
            <div className="pt-3 mt-3 border-t border-white/5 space-y-1.5">
              {original.dates.slice(0, 5).map((d, i) => (
                <div key={i} className="flex justify-between text-xs text-white/50">
                  <span>{format(parseISO(d.date), 'MMM d')}</span>
                  <span>${fmt(d.amount_cents)}</span>
                </div>
              ))}
              {original.dates.length > 5 && (
                <div className="text-white/30 text-xs">+{original.dates.length - 5} more</div>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-5">
        <p className="text-emerald-400/80 text-xs uppercase tracking-wider mb-3">New schedule</p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Per payment</span>
            <span className="text-emerald-400">${fmt(newSchedule?.per_payment_cents ?? 0)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Payments remaining</span>
            <span className="text-emerald-400">{newSchedule?.num_payments ?? 0}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/60">Frequency</span>
            <span className="text-emerald-400">{frequencyLabels[frequency] || frequency}</span>
          </div>
          {newSchedule?.dates?.length > 0 && (
            <div className="pt-3 mt-3 border-t border-white/5 space-y-1.5">
              {newSchedule.dates.slice(0, 5).map((d, i) => (
                <div key={i} className="flex justify-between text-xs text-white/50">
                  <span>{format(parseISO(d.date), 'MMM d')}</span>
                  <span className="text-emerald-400/80">${fmt(d.amount_cents)}</span>
                </div>
              ))}
              {newSchedule.dates.length > 5 && (
                <div className="text-white/30 text-xs">+{newSchedule.dates.length - 5} more</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReserveSwitchReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    selectedFlight,
    returnFlight,
    searchParams,
    bookingOption,
    reserveSwitchTrip
  } = location.state || {};

  // Defera Reserve was discontinued for new users — only existing customers
  // with a wallet balance (or active Reserve trip funds) can use the
  // switch-flight flow.
  const {
    loading: isReserveEligibilityLoading,
    isEligible: isReserveEligible,
  } = useReserveEligibility();

  useEffect(() => {
    if (isReserveEligibilityLoading) return;
    if (!isReserveEligible) {
      navigate('/', { replace: true });
    }
  }, [isReserveEligibilityLoading, isReserveEligible, navigate]);

  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOneWay = !returnFlight && (!searchParams?.slices || searchParams.slices.length === 1);

  const flightData = useMemo(() => {
    if (!selectedFlight) return null;
    const outSlices = selectedFlight?.slices || [];
    const retSlices = returnFlight?.slices || [];
    const slices = retSlices.length > 0 ? [...outSlices, ...retSlices] : outSlices;
    return {
      ...selectedFlight,
      slices,
      _searchParams: searchParams,
      bookingOption,
      _returnFlight: returnFlight
    };
  }, [selectedFlight, returnFlight, searchParams, bookingOption]);

  const { baseFareCents, serviceFeeCents, totalCents } = useMemo(() => {
    const main = bookingOption?.together || bookingOption?.departing;
    const price = parseFloat(main?.price) || 0;
    const base = Math.round(price * 100);
    const service = Math.round(base * 0.11);
    return {
      baseFareCents: base,
      serviceFeeCents: service,
      totalCents: base + service
    };
  }, [bookingOption]);

  useEffect(() => {
    if (!reserveSwitchTrip?.id || !flightData || totalCents == null) {
      if (!reserveSwitchTrip?.id) {
        toast.error('Missing trip information');
        navigate('/app/reserve');
      }
      setIsLoadingPreview(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/home?auth_mode=login');
          return;
        }
        const res = await fetch(reserveUrl('preview-switch-flight'), {
          method: 'POST',
          headers: mergeReserveActAsHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          }),
          body: JSON.stringify({
            trip_id: reserveSwitchTrip.id,
            total_amount_cents: totalCents,
            base_fare_cents: baseFareCents
          })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setPreview(data);
        } else {
          toast.error(data.error || 'Failed to load preview');
        }
      } catch (err) {
        toast.error(err.message || 'Failed to load preview');
      } finally {
        setIsLoadingPreview(false);
      }
    };

    fetchPreview();
  }, [reserveSwitchTrip?.id, totalCents, baseFareCents, navigate]);

  const handleConfirm = async () => {
    if (!reserveSwitchTrip?.id || !flightData) return;

    setIsSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(reserveUrl('switch-flight'), {
        method: 'POST',
        headers: mergeReserveActAsHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }),
        body: JSON.stringify({
          trip_id: reserveSwitchTrip.id,
          flight_data: flightData,
          total_amount_cents: totalCents,
          base_fare_cents: baseFareCents
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to switch flight');

      toast.success('Flight switched! Your payment schedule has been updated.');
      navigate('/app/reserve');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fmt = (c) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const originalTotal = reserveSwitchTrip?.total_amount_cents ?? 0;
  const priceDiff = totalCents - originalTotal;
  const isCheaper = priceDiff < 0;
  const isSame = Math.abs(priceDiff) < 50;

  if (!reserveSwitchTrip) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Missing trip information</p>
          <Button onClick={() => navigate('/app/reserve')} variant="outline">
            Back to Reserve
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Switch Flight – Reserve | Defera</title>
      </Helmet>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8 lg:px-10 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-light">Switch Flight</h1>
              <p className="text-white/60 text-sm">Compare your current reservation with the new selection</p>
            </div>
          </div>

          {/* Flight comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <FlightSummaryCard
              flightData={reserveSwitchTrip.flight_data}
              label="Current reserved flight"
              bookingOption={null}
              searchParams={{ cabin_class: reserveSwitchTrip.cabin_class || 'economy' }}
            />
            <FlightSummaryCard
              flightData={flightData}
              label="New selected flight"
              bookingOption={bookingOption}
              searchParams={searchParams}
            />
          </div>

          {/* Price comparison */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Price comparison</p>
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <span className="text-white/50 text-sm">Original total</span>
                <p className="text-white text-lg">${fmt(originalTotal)}</p>
              </div>
              <div className="text-white/30">→</div>
              <div>
                <span className="text-white/50 text-sm">New total</span>
                <p className="text-white text-lg">${fmt(totalCents)}</p>
              </div>
              <div className="ml-auto">
                {isSame ? (
                  <span className="text-white/60 text-sm">Same price</span>
                ) : isCheaper ? (
                  <span className="text-emerald-400 text-sm font-normal">
                    Saves ${fmt(Math.abs(priceDiff))}
                  </span>
                ) : (
                  <span className="text-brand-400 text-sm font-normal">
                    +${fmt(priceDiff)} more
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Payment schedule comparison */}
          <div className="mb-8">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Payment schedule</p>
            <p className="text-white/65 text-sm mb-4">
              No charge today. Your upcoming payments will be adjusted to reflect the new total.
            </p>
            {isLoadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-white/40" />
              </div>
            ) : preview ? (
              <ScheduleComparison
                original={preview.original_schedule}
                newSchedule={preview.new_schedule}
                frequency={preview.payment_frequency}
              />
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/60 text-sm">
                Could not load schedule preview
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={handleConfirm}
              disabled={isLoadingPreview || isSubmitting}
              className="flex-1 bg-white text-black hover:bg-white/90 h-14 rounded-full font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Switching...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  Confirm switch
                </>
              )}
            </Button>
            <Button
              onClick={() => navigate(-1)}
              className="border border-white/20 bg-white/5 text-white hover:bg-white/10 rounded-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
